import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SearchBarComponent } from '../../../components/search-bar/search-bar.component';
import {
  FilterDropdownComponent,
  FilterDropdownOption,
} from '../../../components/filter-dropdown/filter-dropdown.component';
import {
  SegmentedFilterControlComponent,
  SegmentedFilterItem,
} from '../../../components/segmented-filter-control/segmented-filter-control.component';
import { StatusTab, TabCounts } from '../requirements.types';
import { SavedViewOption, SavedViewsMenuComponent } from '../../../shared/saved-views/saved-views-menu.component';
import { localizeRequirementLabel } from '../../../shared/requirement-label';

@Component({
  selector: 'app-requirements-filter-bar',
  imports: [
    TranslatePipe,
    SearchBarComponent,
    FilterDropdownComponent,
    SegmentedFilterControlComponent,
    SavedViewsMenuComponent,
  ],
  templateUrl: './requirements-filter-bar.component.html',
  styleUrl: './requirements-filter-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequirementsFilterBarComponent {
  searchTerm = input<string>('');
  statusTab = input<StatusTab>(null);
  typeFilter = input<string | null>(null);
  priorityFilter = input<string | null>(null);
  authorFilter = input<string | null>(null);
  statusValueFilter = input<string | null>(null);
  tabCounts = input.required<TabCounts>();
  typeOptions = input<string[]>([]);
  priorityOptions = input<string[]>([]);
  authorOptions = input<string[]>([]);
  statusValueOptions = input<string[]>([]);
  paginatorLength = input<number>(0);
  savedViews = input<SavedViewOption[]>([]);
  activeSavedView = input<string | null>(null);
  projectId = input.required<string>();
  /**
   * Sprach-Signal des Parents. Pflicht — sonst blieben bei Sprachwechsel die
   * Tab-Labels stumm alt (die Komponente hat keine eigene Subscription mehr,
   * fboudouhi Nachfassung).
   */
  currentLang = input.required<string>();

  private readonly translate = inject(TranslateService);

  searchChange = output<string>();
  tabChange = output<StatusTab>();
  typeChange = output<string | null>();
  priorityChange = output<string | null>();
  authorChange = output<string | null>();
  statusValueChange = output<string | null>();
  savedViewSelected = output<string>();
  savedViewSaved = output<string>();
  savedViewDeleted = output<string>();
  savedViewDeleteFailed = output<string>();

  readonly resultCountKey = computed(() =>
    this.tabCounts().total === 1 ? 'requirements.resultCountOne' : 'requirements.resultCountOther',
  );

  readonly statusTabItems = computed<SegmentedFilterItem[]>(() => {
    this.currentLang();
    const counts = this.tabCounts();
    return [
      { id: null, label: this.translate.instant('requirements.tabs.all'), count: counts.total },
      { id: 'offen', label: this.translate.instant('requirements.tabs.open'), count: counts.offen },
      { id: 'review', label: this.translate.instant('requirements.tabs.review'), count: counts.review },
      { id: 'fertig', label: this.translate.instant('requirements.tabs.done'), count: counts.fertig },
    ];
  });

  // NEX-1056 Merge mit NEX-1018 (fboudouhi Nachfassung): der geteilte
  // FilterDropdownComponent rendert opt.label verbatim, daher wird hier
  // vorlokalisiert. Die Uebersetzungslogik liegt in shared/requirement-label —
  // dieselbe Quelle wie fuer RequirementTypeLabelPipe / RequirementStatusLabelPipe.

  // NEX-1056 Review (fboudouhi Blocker 1): Ein aktiver Filterwert, der nicht in
  // der datenabgeleiteten Optionsliste steht (gespeicherte Ansicht mit
  // Bob-Anforderungen, Bobs Anforderungen wurden umverteilt), waere sonst nicht
  // in einem Klick loeschbar — der Browser faellt auf den Placeholder zurueck,
  // ein Klick auf ihn feuert kein change-Event, weil selectedIndex bereits 0
  // ist. Muster gespiegelt aus requirements.component.ts (Orphan-extras).
  private toDropdownOptions(
    values: string[],
    active: string | null,
    label: (v: string) => string = (v) => v,
  ): FilterDropdownOption[] {
    const opts: FilterDropdownOption[] = values.map((v) => ({ value: v, label: label(v) }));
    if (active && !values.includes(active)) opts.push({ value: active, label: label(active) });
    return opts;
  }

  readonly typeDropdownOptions = computed<FilterDropdownOption[]>(() => {
    this.currentLang();
    return this.toDropdownOptions(this.typeOptions(), this.typeFilter(), (v) =>
      localizeRequirementLabel('type', v, this.translate),
    );
  });
  readonly priorityDropdownOptions = computed<FilterDropdownOption[]>(() =>
    this.toDropdownOptions(this.priorityOptions(), this.priorityFilter()),
  );
  readonly authorDropdownOptions = computed<FilterDropdownOption[]>(() =>
    this.toDropdownOptions(this.authorOptions(), this.authorFilter()),
  );
  readonly statusValueDropdownOptions = computed<FilterDropdownOption[]>(() => {
    this.currentLang();
    return this.toDropdownOptions(this.statusValueOptions(), this.statusValueFilter(), (v) =>
      localizeRequirementLabel('status', v, this.translate),
    );
  });

  onSearchChange(term: string): void {
    this.searchChange.emit(term);
  }

  onStatusTabChange(next: string | null): void {
    if (next === null || next === 'offen' || next === 'review' || next === 'fertig') {
      this.tabChange.emit(next);
    }
  }

  onTypeChange(value: string): void {
    this.typeChange.emit(value || null);
  }

  onPriorityChange(value: string): void {
    this.priorityChange.emit(value || null);
  }

  onAuthorChange(value: string): void {
    this.authorChange.emit(value || null);
  }

  onStatusValueChange(value: string): void {
    this.statusValueChange.emit(value || null);
  }

  onSavedViewSelected(name: string): void {
    this.savedViewSelected.emit(name);
  }

  onSavedViewSaved(name: string): void {
    this.savedViewSaved.emit(name);
  }

  onSavedViewDeleted(name: string): void {
    this.savedViewDeleted.emit(name);
  }

  onSavedViewDeleteFailed(name: string): void {
    this.savedViewDeleteFailed.emit(name);
  }
}
